/**
 * Incident Detail Page
 * Detailed view of an incident with investigation notes and attachments
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Incident } from '../types/incident';
import {
  getIncidentById,
  deleteIncident,
  addInvestigationNote,
  uploadAttachment,
  downloadAttachment,
  deleteAttachment,
  verifyHashChain,
  downloadBlob,
} from '../services/incidentService';

const IncidentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Investigation notes
  const [noteContent, setNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Attachments
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Hash verification
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  useEffect(() => {
    if (id) {
      loadIncident();
    }
  }, [id]);

  const loadIncident = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getIncidentById(id!);
      setIncident(data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erreur lors du chargement de l\'incident');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !id) return;

    try {
      setAddingNote(true);
      await addInvestigationNote(id, noteContent);
      setNoteContent('');
      await loadIncident();
    } catch (err: any) {
      alert('Erreur lors de l\'ajout de la note: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setAddingNote(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile || !id) return;

    try {
      setUploadingFile(true);
      await uploadAttachment(id, selectedFile);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      await loadIncident();
    } catch (err: any) {
      alert('Erreur lors de l\'upload: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId: string, filename: string) => {
    try {
      const blob = await downloadAttachment(id!, attachmentId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      alert('Erreur lors du téléchargement: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette pièce jointe ?')) return;

    try {
      await deleteAttachment(id!, attachmentId);
      await loadIncident();
    } catch (err: any) {
      alert('Erreur lors de la suppression: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleVerifyHashChain = async () => {
    try {
      setVerifying(true);
      const result = await verifyHashChain(id!);
      setVerificationResult(result);
    } catch (err: any) {
      alert('Erreur lors de la vérification: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet incident ? Cette action est irréversible.')) {
      return;
    }

    try {
      await deleteIncident(id!);
      navigate('/incidents');
    } catch (err: any) {
      alert('Erreur lors de la suppression: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'NEW':
        return 'bg-blue-100 text-blue-800';
      case 'UNDER_INVESTIGATION':
        return 'bg-purple-100 text-purple-800';
      case 'RESOLVED':
        return 'bg-green-100 text-green-800';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatLabel = (value: string): string => {
    return value
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error || 'Incident non trouvé'}</p>
          </div>
          <button
            onClick={() => navigate('/incidents')}
            className="mt-4 text-sm text-blue-600 hover:text-blue-700"
          >
            ← Retour aux incidents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/incidents')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour aux incidents
          </button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{incident.title}</h1>
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 text-sm font-semibold rounded-full border ${getSeverityColor(
                    incident.severity
                  )}`}
                >
                  {formatLabel(incident.severity)}
                </span>
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(incident.status)}`}>
                  {formatLabel(incident.status)}
                </span>
                <span className="text-sm text-gray-600">{formatLabel(incident.type)}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/incidents/${id}/edit`)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Modifier
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Détails</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Description</label>
                  <p className="mt-1 text-sm text-gray-900">{incident.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Hôtel</label>
                    <p className="mt-1 text-sm text-gray-900">{incident.hotelName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Date de l'incident</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(incident.incidentDate)}</p>
                  </div>
                </div>

                {incident.involvedGuests.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Invités impliqués</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {incident.involvedGuests.map((guestId) => (
                        <span
                          key={guestId}
                          className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded"
                        >
                          {guestId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Signalé par</label>
                    <p className="mt-1 text-sm text-gray-900">{incident.reportedByName || incident.reportedBy}</p>
                  </div>
                  {incident.assignedTo && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Assigné à</label>
                      <p className="mt-1 text-sm text-gray-900">{incident.assignedToName || incident.assignedTo}</p>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <button
                    onClick={handleVerifyHashChain}
                    disabled={verifying}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                  >
                    {verifying ? 'Vérification...' : 'Vérifier l\'intégrité (Hash Chain)'}
                  </button>
                  
                  {verificationResult && (
                    <div className={`mt-2 p-3 rounded-md ${verificationResult.valid ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className={`text-sm ${verificationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                        {verificationResult.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Investigation Notes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes d'investigation</h2>
              
              {/* Add Note Form */}
              <div className="mb-4">
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ajouter une note d'investigation..."
                  disabled={addingNote}
                />
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={handleAddNote}
                    disabled={!noteContent.trim() || addingNote}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingNote ? 'Ajout...' : 'Ajouter une note'}
                  </button>
                </div>
              </div>

              {/* Notes List */}
              <div className="space-y-4">
                {incident.investigationNotes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Aucune note d'investigation</p>
                ) : (
                  incident.investigationNotes.map((note) => (
                    <div key={note.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="text-sm text-gray-900">{note.content}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {note.createdByName || note.createdBy} • {formatDate(note.createdAt)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Attachments */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Pièces jointes</h2>
              
              {/* Upload Form */}
              <div className="mb-4">
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploadingFile}
                />
                <label
                  htmlFor="file-upload"
                  className="block w-full px-4 py-2 text-sm font-medium text-center text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-gray-400"
                >
                  {selectedFile ? selectedFile.name : 'Choisir un fichier'}
                </label>
                {selectedFile && (
                  <button
                    onClick={handleUploadFile}
                    disabled={uploadingFile}
                    className="mt-2 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploadingFile ? 'Upload...' : 'Uploader'}
                  </button>
                )}
              </div>

              {/* Attachments List */}
              <div className="space-y-2">
                {incident.attachments.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Aucune pièce jointe</p>
                ) : (
                  incident.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{attachment.filename}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={() => handleDownloadAttachment(attachment.id, attachment.filename)}
                          className="p-1 text-blue-600 hover:text-blue-700"
                          title="Télécharger"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          className="p-1 text-red-600 hover:text-red-700"
                          title="Supprimer"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Métadonnées</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="font-medium text-gray-600">Créé le</label>
                  <p className="text-gray-900">{formatDate(incident.createdAt)}</p>
                </div>
                <div>
                  <label className="font-medium text-gray-600">Modifié le</label>
                  <p className="text-gray-900">{formatDate(incident.updatedAt)}</p>
                </div>
                <div>
                  <label className="font-medium text-gray-600">ID</label>
                  <p className="text-gray-900 font-mono text-xs">{incident.id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetailPage;
